import { useEffect, useState } from 'react';
import { getLabourOverview, GetLabourOverviewOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, parseISO } from 'date-fns';

type LabourData = GetLabourOverviewOutputType;

const DEPTS = ['Front of House', 'Back of House', 'Kitchen', 'Management'];
const DEPT_SHORT: Record<string, string> = {
  'Front of House': 'FOH',
  'Back of House': 'BOH',
  'Kitchen': 'Kitchen',
  'Management': 'Mgmt',
};

function getCellStyle(count: number) {
  if (count === 0) return 'bg-destructive/10 text-destructive font-bold';
  if (count >= 3) return 'bg-primary/10 text-primary';
  return 'bg-muted text-muted-foreground';
}

export default function LabourOverviewTab() {
  const [data, setData] = useState<LabourData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getLabourOverview({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-48" />
    </div>
  );

  const fmtDay = (d: string) => {
    try { return format(parseISO(d), 'EEE dd'); } catch { return d; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Labour Overview</h2>
          <p className="text-xs text-muted-foreground">Rota coverage from Planday · Next 7 days</p>
        </div>
        <Button size="sm" variant="outline" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {data?.error && (
        <div className="bg-muted border border-border rounded-lg px-4 py-3 text-sm text-muted-foreground">
          ⚠️ {data.error}. Showing empty grid. Configure your Planday API key to see live data.
        </div>
      )}

      {data && (
        <>
          <div className="bg-card border border-border rounded-lg overflow-x-auto p-4">
            <table className="w-full text-xs text-center min-w-[600px]">
              <thead>
                <tr>
                  <th className="text-left font-semibold text-muted-foreground pr-3 pb-2 w-20">Dept</th>
                  {data.days.map(d => (
                    <th key={d} className="font-semibold text-muted-foreground pb-2 px-1">{fmtDay(d)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DEPTS.map(dept => (
                  <tr key={dept}>
                    <td className="text-left font-medium text-foreground pr-3 py-1.5">{DEPT_SHORT[dept]}</td>
                    {data.days.map(day => {
                      const count = data.coverage[dept]?.[day] ?? 0;
                      return (
                        <td key={day} className="py-1 px-1">
                          <span className={`inline-block w-full rounded py-1 text-xs font-medium ${getCellStyle(count)}`}>
                            {count}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.gaps.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Coverage Gaps Detected</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {data.gaps.map(g => `${DEPT_SHORT[g.department]}: ${fmtDay(g.day)}`).join(' · ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {data.gaps.length === 0 && !data.error && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 text-sm text-primary font-medium">
              ✓ No coverage gaps in the next 7 days
            </div>
          )}
        </>
      )}
    </div>
  );
}
